import { useEffect, useState } from "react";
import {
  Search,
  PlusSquare,
  Edit,
  Trash2,
  StickyNoteIcon,
  User as UserIcon,
  Layers,
  XCircle,
  CheckCircle,
  User,
  Phone,
  Mail,
} from "lucide-react";
import Pagination from "../../components/Pagination";
import { useContactUs } from "../../hooks/useContactUs";
import { usePages } from "../../hooks/usePages";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { toast } from "react-toastify";
import Skeleton from "../../components/ui/Skeleton";
import DeleteModal from "../../components/ui/DeleteModal";
import { useParams } from "react-router-dom";
import ContactForm from "./ContactForm";

function ContactList() {
  const [contentsList, setContentsList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedContent, setSelectedContent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("view"); // view, edit, create
  const [deleteModal, setDeleteModal] = useState(false);
  const [pageData, setPageData] = useState({
    currentPage: 1,
    totalPages: 1,
    showing: 10,
    total: 0,
  });
  const [currentCMS, setCurrentCMS] = useState({});
  const { id } = useParams();

  const queryClient = useQueryClient();

  const { createContactUs,updatContactUs,fetchContactUs,toggleIsResolved} =
    useContactUs({
      filters : {
        search_value: searchQuery,
      },
      pagination: { page: pageData.currentPage, limit: 10 },
      sort: { field: "created_at", direction: "desc" },
    });
  const { fetchPageById } = usePages({
    pagination: { page: pageData.currentPage, limit: 10 },
    sort: { field: "created_at", direction: "desc" },
  });

  const contentsQuery = useQuery({
    queryKey: ["contact", pageData.currentPage, searchQuery],
    queryFn: fetchContactUs,
    keepPreviousData: true,
  });

  const pageQuery = useQuery({
    queryKey: ["page", id],
    queryFn: () => fetchPageById(id),
    enabled: !!id,
    onSuccess: (data) => {
      if (data?.data) {
        setSelectedContent(data.data);
      }
    },
  });

  useEffect(() => {
    if (pageQuery.data) {
      const response = pageQuery.data;
      setCurrentCMS(response?.data || {});
    }
  }, [pageQuery.data]);

  useEffect(() => {
    if (contentsQuery.data) {
      const response = contentsQuery.data;
      setContentsList(response?.data?.data || []);
      setPageData({
        currentPage: response?.data?.pagination?.currentPage || 1,
        totalPages: response?.data?.pagination?.lastPage || 1,
        total: response?.data?.pagination?.total || 0,
        showing: response?.data?.pagination?.showing || 10,
      });
    }
  }, [contentsQuery.data]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPageData((prev)=>({
      ...prev,
      currentPage: 1,
    }))
  };

  const openModal = (content, mode) => {
    setSelectedContent(content);
    setModalMode(mode);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedContent(null);
  };

  const handlePageChange = (page) => {
    setPageData((prev) => ({
      ...prev,
      currentPage: page,
      showing: 10,
    }));
  };

  // const handleUpdate = (content) => {
  //   openModal(content, "edit");
  // };

  // const handleDelete = {
  //   onDelete: (content) =>
  //     mutation.deleteContent.mutate(content._id, {
  //       onSuccess: (data) => {
  //         toast.success(data.message || "Content Deleted");
  //         setDeleteModal(false);
  //       },
  //       onError: (error) => {
  //         toast.error(error.message || "Failed to delete content");
  //       },
  //     }),
  //   onClose: () => setDeleteModal(false),
  //   onOpen: (content) => {
  //     setDeleteModal(true);
  //     setSelectedContent(content);
  //   },
  // };

  const mutation = {
    createContactUs: useMutation({
      mutationFn: (body) => {
        if (modalMode === "edit") {
          return updatContactUs(body);
        }
        return createContactUs(body);
      },
    }),
    // deleteContent: useMutation({
    //   mutationFn: (id) => deleteContent(id),
    // }),
    toggleResolved: useMutation({
      mutationFn: ({ id, is_resolved }) => {
        // Assuming there is an updateContactUs API function to update the contact
        return toggleIsResolved(id, { is_resolved });
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["contact", pageData.currentPage]);
        toast.success("Resolved status updated");
      },
      onError: (error) => {
        toast.error(error.message || "Failed to update resolved status");
      },
    }),
  };

  const handleSubmit = (body) => {
    mutation.createContactUs.mutate(body, {
      onSuccess: async (data) => {
        await queryClient.invalidateQueries(["contents"]);
        toast.success(
          data?.data?.message ||
            `Content ${
              modalMode === "edit" ? "Updated" : "Created"
            } successfully`
        );
        setShowModal(false);
      },
      onError: (error) => {
        toast.error(error.message || "Failed to save content");
      },
    });
  };

  

  return (
    <div>
      <main className="">
        {/* <Breadcrumb items={breadcrumbItems} /> */}
        {/* Header and Add Button */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold">{currentCMS?.title} Contact Us Management</h1>
          {/* <button
            onClick={() => openModal({}, "create")}
            className="flex items-center justify-center bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
          >
            <PlusSquare className="mr-2 h-4 w-4" />
            Add New Contact
          </button> */}
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search contents by title..."
                value={searchQuery}
                onChange={handleSearch}
                className="pl-10 pr-4 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Table or Skeleton */}
          {contentsQuery.isLoading ? (
            <Skeleton.Card header rows={10} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Title
                    </th>

                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Contact User
                    </th>

                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Created At
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Updated At
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Resolved
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {contentsList.length > 0 ? (
                    contentsList.map((content) => (
                      <tr
                        key={content._id}
                        className="hover:bg-gray-50 border-b"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                              <Layers className="h-4 w-4 text-gray-600" />
                            </div>
                            <div className="ml-3">
                              <div className="font-medium text-gray-900">
                                {content.reason}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          <button
                            onClick={() => openModal(content.creator, "viewUser")}
                            className="text-blue-400 hover:text-blue-500 cursor-pointer"
                            title="View Contact User"
                          >
                            <UserIcon className="h-5 w-5" />
                          </button>
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-500">
                          {moment(content.created_at).format(
                            "MMMM Do YYYY, h:mm:ss a"
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {moment(content.updated_at).format(
                            "MMMM Do YYYY, h:mm:ss a"
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {mutation.toggleResolved.isPending ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              <svg
                                className="animate-spin h-4 w-4 text-gray-500"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                                ></path>
                              </svg>
                              <span className="ml-1">Updating</span>
                            </span>
                          ) : (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer select-none ${
                                content.is_resolved
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                              onClick={() => {
                                mutation.toggleResolved.mutate({
                                  id: content._id,
                                  is_resolved: !content.is_resolved,
                                });
                              }}
                              title="Toggle Resolved"
                            >
                              {content.is_resolved ? (
                                <CheckCircle className="mr-1 h-3 w-3" />
                              ) : (
                                <XCircle className="mr-1 h-3 w-3" />
                              )}
                              {content.is_resolved ? "Resolved" : "Unresolved"}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => openModal(content, "view")}
                              className="text-gray-400 hover:text-gray-500"
                              title="View Details"
                            >
                              <StickyNoteIcon className="h-4 w-4" />
                            </button>
                            {/* <button
                              onClick={() => openModal(content.creator, "viewUser")}
                              className="text-blue-400 hover:text-blue-500"
                              title="View Contact User"
                            >
                              Contact User Info
                            </button> */}
                            {/* <button
                              onClick={() => handleUpdate(content)}
                              className="text-blue-400 hover:text-blue-500"
                              title="Edit Content"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              className="text-red-400 hover:text-red-500"
                              onClick={() => handleDelete.onOpen(content)}
                              title="Delete Content"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button> */}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No contents found matching your search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            currentPage={pageData.currentPage}
            onPageChange={handlePageChange}
            showing={pageData.showing}
            totalPages={pageData.totalPages}
            total={pageData.total}
          />
        </div>
      </main>
      <DeleteModal
        isOpen={deleteModal}
        // onClose={handleDelete.onClose}
        // onDelete={() => handleDelete.onDelete(selectedContent)}
        title="Content"
      />
          {showModal && (
            <div className="fixed inset-0 flex items-center justify-center z-50">
              <div
                className="absolute inset-0 bg-black bg-opacity-50"
                onClick={closeModal}
              ></div>
              <div className="h-4/5 relative bg-white rounded-2xl max-w-4xl w-full mx-4 shadow-xl overflow-auto">
                <div className="px-6 py-4 border-b">
                  <h3 className="text-lg font-medium">
                    {modalMode === "view"
                      ? "Contact Us Query"
                      : modalMode === "edit"
                      ? "Edit Content"
                      : modalMode === "create"
                      ? "Create Content"
                      : modalMode === "viewUser"
                      ? "Contact User Info"
                      : ""}
                  </h3>
                  <button
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
                    onClick={closeModal}
                  >
                    <XCircle className="h-5 w-5" />
                  </button>
                </div>

                <div className="p-6 space-y-4">
                  {modalMode === "viewUser" ? (
                    <div>
                      <label className="block text-sm font-medium mb-1">Full Name</label>
                      <div className="relative">
                        <User className="absolute left-3 h-5 w-5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          value={selectedContent?.full_name || ""}
                          readOnly={true}
                          className="pl-10 pr-3 py-2 border rounded w-full"
                        />
                      </div>
                      <label className="block text-sm font-medium mt-4 mb-1">Phone</label>
                      <div className="relative">
                        <Phone className="absolute left-3 h-5 w-5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          value={selectedContent?.phone || ""}
                          readOnly={true}
                          className="pl-10 pr-3 py-2 border rounded w-full"
                        />
                      </div>
                      <label className="block text-sm font-medium mt-4 mb-1">Email</label>
                      <div className="relative">
                        <Mail className="absolute left-3 h-5 w-5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          value={selectedContent?.email || "Not Available"}
                          readOnly={true}
                          className="pl-10 pr-3 py-2 border rounded w-full"
                        />
                      </div>
                    </div>
                  ) : (
                    <ContactForm
                      mode={modalMode}
                      defaultValues={
                        selectedContent
                          ? {
                              ...selectedContent,
                              created_at: moment(selectedContent.created_at).format(
                                "YYYY-MM-DD"
                              ),
                              contentIds: selectedContent.contentIds || [],
                            }
                          : {}
                      }
                      onCancel={closeModal}
                      onSubmit={(data) => handleSubmit(data)}
                      // submissionLoading={mutation.createContent.isLoading}
                      CMSData={currentCMS}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
    </div>
  );
}

export default ContactList;
